<template>
    <div 
        v-if="!isDisabledForNightProducts" 
        :class="['autorenewal', { 'autorenewal--disabled': isDisabled }]"
    >
        <!-- Auto renewal blocked (in post listing and post manage) -->
        <ui-alert
            v-if="isBlocked"
            type="error"
            :title="i18n.autorenewalBlocked"
            small-description
        >
            <template #description>
                {{ i18n.autorenewalBlockedDescription }} <a :href="purchaseCreditsUrl" class="text-uppercase">{{ i18n.purchaseCredits }}</a>
            </template>
        </ui-alert>
        <!-- Auto renewal active (in post listing) -->
        <ui-alert 
            v-else-if="isActive && isPostList" 
            type="success"
            :title="i18n.autorenewalActive"
        />
        <!-- In post manage -->
        <div class="autorenewal__box" v-else-if="!isPostList">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <i class="fa fa-refresh" aria-hidden="true"></i>
                    {{ i18n.autorenewal }}
                    <p v-if="userWithEnoughCredits" class="small m-0">{{ i18n.autorenewalDescription }}</p>
                    <template v-else>
                        <p class="small m-0 text-error">{{ i18n.autorenewalBlockedDescription }}</p>
                        <a v-if="!isCheckoutPage" :href="purchaseCreditsUrl" class="small">{{ i18n.purchaseCredits }}</a>
                    </template>
                </div>
                <label class="switch switch-left-right">
                    <input
                        id="autorenewal-checkbox"
                        class="switch-input"
                        type="checkbox"
                        :checked="autorenewalIsActive"
                        @click.prevent="handleChange($event.target.checked)"
                        :disabled="checkboxIsDisabled">
                    <span
                        class="switch-label"
                        :class="{ disabled: checkboxIsDisabled }"
                        :data-on="i18n.yes"
                        :data-off="i18n.no">
                    </span>
                    <span class="switch-handle" :class="{ disabled: checkboxIsDisabled }">
                    </span>
                </label>
            </div>
            <ui-alert
                v-if="error"
                type="error"
                dismissible
                class="mt-3"
                @close="clearError"
                :title="i18n.anErrorOccurred"
            />
            <div class="promo-banner mt-3">
                <div class="badger-right">
                    <span class="badge badge-new">{{ i18n.new }}</span>
                </div>
                <div class="promo-banner-container">
                    <h6>{{ i18n.freeCoupon }}</h6>
                    <a @click.prevent="handleLearnMore" href="#">{{ i18n.learnMore }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";

export default {
    name: 'AutoRenewal',
    components: {
        UiAlert: () => import(/* webpackChunkName: "chunk-ui-alert" */ '../../ui/ui-alert.vue'),
    },
    props: {
        isPostList: {
            type: Boolean,
            default: false,
        },
        isBlocked: {
            type: Boolean,
            default: false,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        purchaseCreditsUrl: {
            type: String,
            required: true,
        },
        apiUrl: {
            type: String,
            required: true,
        },
        freeCouponThreshold: {
            type: Number,
            default: 10,
        },
        promotionName: {
            type: String,
            default: '',
            required: false,
        },
        promotionNameWithAddons: {
            type: String,
            default: '',
            required: false,
        },
        promotionTime: {
            type: String,
            required: true,
        },
        promotionCredits: {
            type: Number,
            required: true,
        },
        promotionTimeRange: {
            type: Number,
            required: false,
            default: null,
        },
        postPrivateCode: {
            type: String,
            required: true
        },
        availableCredits: {
            type: Number,
            required: true,
            default: 0,
        },
        hideForNightProducts: {
            type: Boolean,
            default: false,
        },
        availableAddons: {
            type: Array,
            required: false,
            default: () => [],
        },
        selectedAddons: {
            type: Array,
            required: false,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            i18n: {
                autorenewal: gettext('Auto-Renewal'),
                autorenewalDescription: gettext('The promotion will renew automatically, until the credits in the wallet are exhausted.'),
                autorenewalBlocked: gettext('Auto-Renewal Blocked'),
                autorenewalBlockedDescription: gettext('You do not have enough credits to use Auto-Renewal.'),
                autorenewalActive: gettext('Auto-Renewal Active'),
                autorenewalDisabled: gettext('Auto-Renewal Disabled'),
                anErrorOccurred: gettext('An unexpected error has occurred'),
                yes: gettext('Yes'),
                no: gettext('No'),
                new: gettext('New'),
                learnMore: gettext('Learn more'),
                purchaseCredits: gettext('Purchase Credits'),
                freeCoupon: gettext('Get a FREE promo coupon of the same type every %(renewals_counter)s renewals').replace("%(renewals_counter)s", this.freeCouponThreshold),
            },
            isDisabled: this.disabled || false,
            error: null,
            autorenewalIsActive: this.isActive || false,
        }
    },
    computed: {
        currentPromotion() {
            return this.$store.getters['autorenewal/getPromotion'];
        },
        isNightProduct() {
            if(this.promotionTimeRange === null) return false;

            if(this.promotionTimeRange === 1) return true;

            return false;
        },
        isDisabledForNightProducts() {
            return this.isNightProduct && this.hideForNightProducts;
        },
        userWithEnoughCredits() {
            return this.availableCredits >= this.promotionCredits;
        },
        checkboxIsDisabled() {
            return this.isDisabled || (!this.userWithEnoughCredits && !this.autorenewalIsActive);
        },
        isCheckoutPage() {
            return window.location.href.includes('order-checkout') || false;
        }
    },
    watch: {
        // currentPromotion changes after autorenewal API call response
        // This pattern help us to update UI properly
        currentPromotion(newCurrentPromotion, oldCurrentPromotion) {
            if(newCurrentPromotion && oldCurrentPromotion) {
                if(newCurrentPromotion.error) {
                    this.error = newCurrentPromotion?.error;
                }
                else if(newCurrentPromotion.autorenewalIsActive !== oldCurrentPromotion.autorenewalIsActive) {
                    // Update current autorenewalIsActive state based on api response
                    this.autorenewalIsActive = newCurrentPromotion.autorenewalIsActive;
                    if(this.error) {
                        this.clearError();
                    }
                }
            }
            this.enableUserInteraction();
        }
    },
    methods: {
        handleLearnMore() {
            this.$store.dispatch("ui/lightboxManager/show", "lightbox_autorenewal_learn_more");
        },
        handleChange() {
            this.disableUserInteraction();
            if (window.hj) {
              const ev = this.autorenewalIsActive ? 'disabling' : 'enabling';
              window.hj('event', `autorenewal-${ev}`);
            }

            this.$store.dispatch("autorenewal/setCurrentPromotion", {
                apiUrl: this.apiUrl,
                name: this.promotionNameWithAddons || this.promotionName,
                timeRange: this.promotionTimeRange,
                isNightProduct: this.isNightProduct,
                time: this.promotionTime,
                credits: this.promotionCredits,
                privateCode: this.postPrivateCode,
                error: null,
                autorenewalIsActive: this.autorenewalIsActive,
                availableAddons: this.availableAddons,
                selectedAddons: this.selectedAddons,
            });

            this.$store.dispatch("ui/lightboxManager/show", "lightbox_autorenewal");
        },
        handleLearnMore() {
            this.$store.dispatch("ui/lightboxManager/show", "lightbox_autorenewal_learn_more");
        },
        disableUserInteraction() {
            this.isDisabled = true;
        },
        enableUserInteraction() {
            this.isDisabled = false;
        },
        clearError() {
            this.error = null;
            this.$store.dispatch("autorenewal/setCurrentPromotion", {
                ...this.currentPromotion,
                error: null,
            });
        }
    }
};
</script>

<style>
.autorenewal__box {
    position: relative;
    padding: .75rem 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid #dee2e6;
    border-radius: .25rem;
    background-color: #fefefe;
    color: black;
}
</style>